import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "./AuthContext"; // Access authentication state
import { useCompletedTimers } from "../context/CompletedTimersContext"; // Access completed timers
import "./NavBar.css";

const NavBar = React.memo(({ onLogout }) => {
  const { cartItemCount } = useShoppingCart();
  const [isOpen, setIsOpen] = useState(false);
  const { token, user } = useAuth();
  const { completedTimers } = useCompletedTimers();
  const navigate = useNavigate();
  const navbarRef = useRef(null);

  // Handle clicks outside the navbar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the menu is open and the click is outside the navbar
      if (
        isOpen &&
        navbarRef.current &&
        !navbarRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener when the menu is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar__logo">
        <Link to="/" onClick={handleLinkClick}>
          <img
            src="/images/dairls-logo-app.svg"
            alt="Studio Logo"
            className="studio-logo"
          />
        </Link>
      </div>
      <div className={`navbar__links ${isOpen ? "open" : ""}`}>
        <Link to="/" onClick={handleLinkClick}>
          Home
        </Link>
        <Link to="/about" onClick={handleLinkClick}>
          About Us
        </Link>
        <Link to="/products" onClick={handleLinkClick}>
          Memberships & Products
        </Link>
        <Link to="/contact" onClick={handleLinkClick}>
          Contact
        </Link>
        {/* FEEDBACK-SOFT-HIDE-001: Customer-facing "Feedback" link removed.
            Backend Feedback model + endpoints intact for Testimonials.js
            historical read path. */}

        {/* Render the cart icon if there are items */}
        {cartItemCount > 0 && (
          <Link to="/cart">
            <div className="cart-icon">
              <FontAwesomeIcon icon={faShoppingCart} />
              <span className="cart-count">{cartItemCount}</span>
            </div>
          </Link>
        )}
        <>
          {/* Add Blog link for all users */}
          <Link
            to={token ? "/blog" : "/blog-preview"}
            onClick={handleLinkClick}
          >
            Blog
          </Link>
          {/* For staff/admin, add a link to blog management */}
          {user && (user.role === "admin" || user.role === "staff") && (
            <Link to="/staff/blog" onClick={handleLinkClick}>
              Blog Management
            </Link>
          )}
          {/* For blogger navigation */}
          {user && user.role === "blogger" && (
            <Link to="/blogger" onClick={handleLinkClick}>
              Blogger Dashboard
            </Link>
          )}

          {/* For bloggers, add a link to create posts */}
          {user && user.role === "blogger" && (
            <Link to="/staff/blog/create" onClick={handleLinkClick}>
              Create Post
            </Link>
          )}
        </>

        {token ? (
          <>
            <Link
              to={
                user && (user.role === "staff" || user.role === "admin")
                  ? "/staff"
                  : user && user.role === "massage_therapist"
                  ? "/therapist"
                  : user && user.role === "luminary"
                  ? "/luminary"
                  : "/member"
              }
              onClick={handleLinkClick}
              className="dashboard-link"
            >
              Dashboard
              {user &&
                (user.role === "staff" || user.role === "admin") &&
                completedTimers.length > 0 && (
                  <span className="nav-badge">{completedTimers.length}</span>
                )}
            </Link>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </>
        ) : (
          //Login and Register functionality have been disabled:
          <>
            <Link to="/login" onClick={handleLinkClick}>
              Login
            </Link>
            <Link to="/register" onClick={handleLinkClick}>
              Register
            </Link>
          </>
        )}
      </div>
      <div className="navbar__toggle" onClick={toggleMenu}>
        <span className="bar"></span>
        <span className="bar"></span>
        <span className="bar"></span>
      </div>
    </nav>
  );
});

export default NavBar;
